/**
 * Channel API Client
 * Handles all API calls for Channel entities
 */

import { BaseApiClient, ApiResponse } from './baseApiClient';
import type { StandardListRequest, GetByIdRequest } from '../types';

export interface Channel {
  ChannelID: number;
  Name: string;
  ChannelName?: string; // API may return ChannelName instead of Name
  Description?: string;
  IsActive: boolean;
  CreatedOn?: string;
}

export class ChannelApiClient extends BaseApiClient {
  /**
   * Get all active channels for current tenant
   */
  async getActive(request: StandardListRequest = {}): Promise<ApiResponse<Channel[]>> {
    const requestWithPagination = {
      PageInfo: {
        PageNumber: 1,
        PageSize: 1000,
      },
      ...request,
    };
    return this.postQuery<Channel[]>('/api/v1/ai-channel/channels/active', requestWithPagination);
  }

  /**
   * Get channel by ID
   */
  async getById(request: GetByIdRequest): Promise<ApiResponse<Channel>> {
    const webRequest = {
      ID: {
        ID: request.id,
      },
    };
    return this.postQuery<Channel>('/api/v1/ai-channel/channels/get-by-id', webRequest);
  }

  /**
   * Create new channel
   */
  async create(channel: any): Promise<ApiResponse<Channel>> {
    const response = await this.post<any>('/api/v1/ai-channel/channels', channel);

    // Handle response structure - API returns id in response.id.id or response.data.Id.id
    if (response.success) {
      let channelId = null;

      // Try to extract ID from various possible locations
      const r = response as any;
      if (r.id?.id) {
        channelId = r.id.id;
      } else if (response.data?.id?.id) {
        channelId = response.data.id.id;
      } else if (response.data?.Id?.id) {
        channelId = response.data.Id.id;
      }

      if (channelId) {
        return {
          success: true,
          data: {
            ChannelID: channelId,
            ...channel,
            IsActive: channel.IsActive ?? true,
          } as Channel,
        };
      }
    }

    // If we couldn't extract the ID but still got success, return what we have
    if (response.success && !response.data?.ChannelID) {
      return {
        success: true,
        data: {
          ChannelID: 0, // Fallback - will be updated from actual data
          ...channel,
          IsActive: channel.IsActive ?? true,
        } as Channel,
      };
    }

    return response;
  }

  /**
   * Update existing channel
   */
  async update(id: number, channel: any): Promise<ApiResponse<Channel>> {
    const webRequest = {
      Data: {
        ChannelID: id,
        ...channel,
      },
    };
    return this.put<Channel>(`/api/v1/ai-channel/channels/${id}`, webRequest);
  }

  /**
   * Delete channel
   */
  async deleteById(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      ID: {
        ID: id,
      },
    };
    return this.delete<void>(`/api/v1/ai-channel/channels/${id}`, webRequest);
  }

  /**
   * Activate channel
   */
  async activate(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      ChannelID: {
        ID: id,
      },
    };
    return this.post<void>('/api/v1/ai-channel/channels/activate', webRequest);
  }

  /**
   * Deactivate channel
   */
  async deactivate(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      ChannelID: {
        ID: id,
      },
    };
    return this.post<void>('/api/v1/ai-channel/channels/deactivate', webRequest);
  }
}

export const channelApiClient = new ChannelApiClient();
